import { PrizeStatus } from '@daymade/common-lib/dbEnums';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { booleanParamSchema, successSchema } from '../utils';
import {
  ZPrizeCore,
  ZPrizeDisplayGroup,
  ZPrizeInsert,
  ZPrizeSelect,
  ZProductInsert,
  ZProductSelect,
} from './models';

const c = initContract();

// expose for frontend validation
export const ZCreatePrizeRequest = ZPrizeInsert.extend({
  sortOrder: z.number().optional(),
  title: ZPrizeInsert.shape.title.trim().min(1, {
    message: 'Title must contain at least 1 non-whitespace character.',
  }),
  subheading: ZPrizeInsert.shape.subheading.trim().min(1, {
    message: 'Subheading must contain at least 1 non-whitespace character.',
  }),
  slug: ZPrizeInsert.shape.slug
    .min(1, 'Slug must have at least 1 character')
    .refine(
      (slug) => {
        const sanitizedSlug = slug.replace(/[^\w-]|_/g, '');
        return slug === sanitizedSlug;
      },
      {
        message:
          'Slug contains invalid characters. Only alphanumeric characters and hyphens are allowed.',
      },
    ),
  imageLocations: ZPrizeInsert.shape.imageLocations.extend({
    main: ZPrizeInsert.shape.imageLocations.shape.main
      .trim()
      .url('Main image is required and must have a valid URL'),
  }),
  whatsIncludedBody: ZPrizeInsert.shape.whatsIncludedBody.refine(
    (html) => {
      const text = html.replace(/<[^>]*>/g, '').trim(); // Remove HTML tags
      return text.length !== 0;
    },
    {
      message: "What's Included Body must not be empty.",
    },
  ),
  seoKeywords: ZPrizeInsert.shape.seoKeywords.trim().min(1, {
    message: 'SEO Keywords must contain at least 1 non-whitespace character.',
  }),
  seoImageAltText: ZPrizeInsert.shape.seoImageAltText.extend({
    main: ZPrizeInsert.shape.seoImageAltText.shape.main.trim().min(1, {
      message:
        'Main Image Alt Text must contain at least 1 non-whitespace character',
    }),
  }),
  // vendorCategory is nullable in DB as old prizes don't have it set
  vendorCategory: ZPrizeInsert.shape.vendorCategory.refine(
    (value) => value !== null,
    {
      message: 'Required',
    },
  ),
  products: ZProductInsert.extend({
    name: ZProductInsert.shape.name.trim().min(1, {
      message: 'Product name must contain at least 1 non-whitespace character.',
    }),
    price: ZProductInsert.shape.price.refine((price) => price && price > 0, {
      message: 'Product price is required and must be greater than 0.',
    }),
  }).array(),
  categories: z.string().array(),
  tiers: z.record(z.string(), z.string().nullable()),
});

export const ZUpdatePrizeRequest = ZCreatePrizeRequest.partial().required({
  id: true,
});

export const adminPrizeContract = c.router(
  {
    createPrize: {
      method: 'POST',
      path: '/',
      body: ZCreatePrizeRequest,
      responses: {
        200: z.object({ id: z.number(), slug: z.string() }),
        400: c.type<{ message: string }>(),
      },
      summary: 'Create a prize',
    },

    updatePrize: {
      method: 'PATCH',
      path: '/',
      body: ZUpdatePrizeRequest,
      responses: {
        200: successSchema,
        400: c.type<{ message: string }>(),
      },
      summary: 'Update a prize',
    },

    getPrize: {
      method: 'GET',
      path: '/details/:id',
      pathParams: z.object({
        id: z.coerce.number(),
      }),
      responses: {
        200: ZPrizeSelect.omit({
          createdAt: true,
          updatedAt: true,
          legacyPrizeValue: true,
        }).extend({
          products: ZProductSelect.omit({
            createdAt: true,
            updatedAt: true,
          }).array(),
          categories: z.string().array(),
          tiers: z.record(z.string(), z.string()),
        }),
        404: z.object({
          message: z.string(),
        }),
      },
      summary: 'Get a prize by id',
    },

    getPrizes: {
      method: 'GET',
      path: '/prizes',
      responses: {
        200: ZPrizeCore.extend({
          status: z.nativeEnum(PrizeStatus),
          sortOrder: z.number().nullable(),
        })
          .extend({
            tiers: z.record(z.string(), z.string()).optional(),
          })
          .array(),
      },
      summary: 'Get all prizes with prize tiers',
    },

    getPrizesInPrizeTier: {
      method: 'GET',
      path: '/prize-tier/:id/prizes',
      pathParams: z.object({
        id: z.coerce.number(),
      }),
      responses: {
        200: ZPrizeCore.merge(
          ZPrizeSelect.pick({
            status: true,
            overviewBody: true,
            whatsIncludedBody: true,
          }).extend({
            lastUsedInDraw: z
              .object({
                drawSchedule: z.string(),
                lastUsedAt: z.coerce.date().nullable(),
              })
              .array(),
            rotationOrder: z.number().nullable().optional(),
          }),
        ).array(),
      },
      summary: 'Get prizes in a prize tier',
    },

    getPrizesInDisplayGroups: {
      method: 'GET',
      path: '/prize-display-ordering/prizes',
      query: z.object({
        groups: ZPrizeDisplayGroup.array(),
        status: z.nativeEnum(PrizeStatus).optional(),
        prizeTierId: z.coerce.number().optional(),
      }),
      responses: {
        200: z.record(
          ZPrizeDisplayGroup,
          ZPrizeCore.extend({
            status: z.nativeEnum(PrizeStatus),
            position: z.number(),
          }).array(),
        ),
      },
      summary: 'Get prizes in display groups',
    },

    getAllCategories: {
      method: 'GET',
      path: '/get-all-categories',
      responses: {
        200: z.record(z.string(), z.string().array()),
      },
      summary: 'Get all prize categories, grouped by main category',
    },

    getPrizeTiers: {
      method: 'GET',
      path: '/get-prize-tiers',
      query: z.object({
        prizeStructure: z.string().optional(),
        isRotatable: booleanParamSchema.optional(),
      }),
      responses: {
        200: z
          .object({
            id: z.number(),
            prizeStructure: z.string(),
            prizeTier: z.string(),
          })
          .array(),
      },
      summary: 'Get prize tiers',
    },

    uploadPrizePhoto: {
      method: 'POST',
      path: '/upload-prize-photo',
      contentType: 'multipart/form-data',
      body: c.type<{ image: File }>(),
      responses: {
        200: z.string(),
        400: z.object({
          message: z.string(),
        }),
      },
    },

    addPrizeToDisplayGroup: {
      method: 'POST',
      path: '/prize-display-ordering/:group/prize/:prizeId',
      pathParams: z.object({
        group: ZPrizeDisplayGroup,
        prizeId: z.coerce.number(),
      }),
      body: z.object({}),
      responses: {
        200: successSchema,
      },
    },

    removePrizeFromDisplayGroup: {
      method: 'DELETE',
      path: '/prize-display-ordering/:group/prize/:prizeId',
      pathParams: z.object({
        group: ZPrizeDisplayGroup,
        prizeId: z.coerce.number(),
      }),
      responses: {
        200: successSchema,
      },
    },

    reorderPrizeInDisplayGroup: {
      method: 'POST',
      path: '/prize-display-ordering/:group/prize/:prizeId/reorder',
      pathParams: z.object({
        group: ZPrizeDisplayGroup,
        prizeId: z.coerce.number(),
      }),
      body: z.object({
        position: z.coerce.number(),
      }),
      responses: {
        200: successSchema,
      },
    },
  },
  {
    pathPrefix: '/admin',
    // baseHeaders: z.object({
    //   'x-access-token': z.string(),
    // }),
    commonResponses: {
      401: c.type<{ message: string; statusCode: 401 }>(),
      403: c.type<{ message: string; statusCode: 403 }>(),
      // 500: c.otherResponse({
      //   contentType: 'text/plain',
      //   body: z.literal('Server Error'),
      // }),
    },
  },
);
